/**
 * marketEquilibrium — 28/09/2026 (demande user : « la courbe de croissance
 * est trop exponentielle, la marge ne doit pas dépasser 65 % »).
 *
 * Plafond d'équilibre de marché : chaque jour ouvré, pour CHAQUE contrat
 * actif (AO inclus), on calcule la marge brute réelle = (revenu effectif
 * bonus inclus − charges variables de la mission) / revenu effectif. Les
 * charges variables sont celles de la modale d'assignation
 * (utils/contractMargin : carburant, entretien, salaire, assurance, péages).
 *
 * Au-dessus de 65 %, le client renégocie : le tarif journalier baisse de
 * 3 % par jour au maximum jusqu'à converger vers le prix qui donne
 * exactement 65 % (jamais en dessous). Le contrat porte `marketPressure`
 * (badge « Marché sous tension ») et garde `originalDailyRevenue`.
 */
import { computeContractMargin } from '../../utils/contractMargin';
import { computeContractBonusMultiplier } from './contractBonusStack';
import { effectiveGlobalBonus } from '../../data/economyBalance';
import { INVESTMENTS } from '../../data/investments';

export const MARKET_EQUILIBRIUM_MAX_GROSS_MARGIN = 0.65;
export const MARKET_EQUILIBRIUM_DAILY_STEP = 0.03;

export function computeGlobalRevenueFactor(state, supportRevenueBonus = 0) {
  const marketingBonus = Object.keys(state.activeInvestments || {}).reduce((acc, invId) => {
    const inv = INVESTMENTS.find(i => i.id === invId);
    return inv && inv.impactMultiplier > 0 ? acc + inv.impactMultiplier : acc;
  }, 0);
  return 1 + effectiveGlobalBonus(marketingBonus + supportRevenueBonus);
}

/** Marge brute réelle d'un contrat actif (bonus inclus). null si non calculable. */
export function computeContractGrossMargin(state, contract, vehicle, driver, opts = {}) {
  if (!contract || !vehicle || !driver || !(contract.dailyRevenue > 0)) return null;
  const base = computeContractMargin(contract, vehicle, driver);
  if (!base || base.estimated || !(base.durationDays > 0)) return null;
  const dailyCost = base.charges / base.durationDays;
  if (!(dailyCost > 0)) return null;
  const factor = computeContractBonusMultiplier(state, contract, vehicle, driver)
    * (opts.globalFactor || 1)
    * (opts.difficultyMultiplier || 1);
  const effectiveDaily = contract.dailyRevenue * factor;
  return {
    dailyCost,
    factor,
    effectiveDaily,
    marginPct: (effectiveDaily - dailyCost) / effectiveDaily,
  };
}

/**
 * Applique la renégociation du jour sur la liste de contrats actifs.
 * Pur : ne touche pas au state, renvoie la nouvelle liste + diagnostics.
 */
export function applyMarketEquilibrium(state, contracts, working, opts = {}) {
  const list = contracts || [];
  if (!working) return { contracts: list, renegotiatedIds: [], pressuredCount: countPressured(list), snapshot: null };
  const globalFactor = computeGlobalRevenueFactor(state, opts.supportRevenueBonus || 0);
  const difficultyMultiplier = opts.difficultyMultiplier || 1;
  const today = state.totalDays || 0;
  const renegotiatedIds = [];
  let weightedMargin = 0;
  let weightSum = 0;
  let measured = 0;
  let overCapCount = 0;
  const next = list.map((c) => {
    const v = (state.vehicles || []).find(x => x.id === c.vehicleId);
    const d = (state.drivers || []).find(x => x.id === c.driverId);
    const gm = computeContractGrossMargin(state, c, v, d, { globalFactor, difficultyMultiplier });
    if (!gm) return c;
    measured += 1;
    weightedMargin += gm.marginPct * gm.effectiveDaily;
    weightSum += gm.effectiveDaily;
    if (gm.marginPct <= MARKET_EQUILIBRIUM_MAX_GROSS_MARGIN) return c;
    overCapCount += 1;
    const targetDaily = gm.dailyCost / (1 - MARKET_EQUILIBRIUM_MAX_GROSS_MARGIN) / gm.factor;
    const stepped = c.dailyRevenue * (1 - MARKET_EQUILIBRIUM_DAILY_STEP);
    const nextDaily = Math.round(Math.max(targetDaily, stepped));
    if (nextDaily >= c.dailyRevenue) return c;
    const originalDailyRevenue = c.originalDailyRevenue || c.dailyRevenue;
    renegotiatedIds.push(c.instanceId);
    return {
      ...c,
      dailyRevenue: nextDaily,
      originalDailyRevenue,
      marketPressure: {
        since: c.marketPressure?.since ?? today,
        lastDay: today,
        reductionPct: Math.round((1 - nextDaily / originalDailyRevenue) * 100),
        grossMarginPct: Math.round(gm.marginPct * 100),
      },
    };
  });
  const pressuredCount = countPressured(next);
  const snapshot = measured > 0 ? {
    day: today + 1,
    grossMarginPct: Math.round((weightedMargin / weightSum) * 1000) / 10,
    contracts: measured,
    overCapCount,
    pressuredCount,
    dailyRevenue: Math.round(weightSum),
    foregone: Math.round(next.reduce((s, c) => s + Math.max(0, (c.originalDailyRevenue || 0) - (c.dailyRevenue || 0)), 0)),
  } : null;
  return { contracts: next, renegotiatedIds, pressuredCount, snapshot };
}

export const MARGIN_HISTORY_MAX = 360;

/** Historique quotidien de la marge brute moyenne (tableau borné). */
export function appendMarginHistory(history, snapshot) {
  if (!snapshot) return history || [];
  return [...(history || []), snapshot].slice(-MARGIN_HISTORY_MAX);
}

export function countPressured(contracts) {
  return (contracts || []).filter(c => c && c.marketPressure).length;
}
